import { callProxy } from './supabase';

export interface NotificationFilters {
  levels?: string[]; // ['L1', 'L2']
  xLinked?: 'all' | 'linked' | 'unlinked';
  tgLinked?: 'all' | 'linked' | 'unlinked';
  gmailLinked?: 'all' | 'linked' | 'unlinked';
  specificWallets?: string[];
}

/**
 * Lấy danh sách ví mục tiêu dựa trên bộ lọc
 */
export async function getTargetWallets(filters: NotificationFilters): Promise<string[]> {
  try {
    // 1. Lấy dữ liệu cơ bản
    const [wallets, socialAccounts] = await Promise.all([
      callProxy({ action: 'select', table: 'wallets', select: 'wallet_address, user_id' }),
      callProxy({ action: 'select', table: 'linked_social_accounts', select: 'user_id, provider, verified' })
    ]);

    if (!wallets || wallets.length === 0) return [];

    // Nếu có danh sách ví cụ thể -> CHỈ gửi cho danh sách này, bỏ qua các bộ lọc khác
    if (filters.specificWallets && filters.specificWallets.length > 0) {
      const specificSet = new Set(filters.specificWallets.map(w => w.toLowerCase().trim()));
      return wallets
        .map((w: any) => w.wallet_address.toLowerCase())
        .filter((address: string) => specificSet.has(address));
    }

    // 2. Chế biến dữ liệu social để lọc theo tiêu chí
    const userSocialMap = new Map<string, any>();
    socialAccounts?.forEach((acc: any) => {
      if (!userSocialMap.has(acc.user_id)) {
        userSocialMap.set(acc.user_id, { x: false, tg: false, gmail: false });
      }
      const status = userSocialMap.get(acc.user_id);
      const provider = acc.provider.toLowerCase();
      if (['x', 'twitter'].includes(provider)) status.x = true;
      if (provider === 'telegram' && acc.verified) status.tg = true;
      if (['google', 'gmail'].includes(provider)) status.gmail = true;
    });

    // 3. Lọc danh sách ví theo bộ lọc
    const filteredWallets = wallets.filter((w: any) => {
      const social = userSocialMap.get(w.user_id) || { x: false, tg: false, gmail: false };
      
      // Tính level
      let levelValue = 0;
      if (social.x && social.tg && social.gmail) {
        levelValue = 2;
      } else if (social.x || social.tg) {
        levelValue = 1;
      }
      const levelStr = levelValue === 2 ? 'L2' : levelValue === 1 ? 'L1' : 'L0';

      // Lọc theo Level
      if (filters.levels && filters.levels.length > 0 && !filters.levels.includes(levelStr)) {
        return false;
      }

      // Lọc theo X
      if (filters.xLinked === 'linked' && !social.x) return false;
      if (filters.xLinked === 'unlinked' && social.x) return false;

      // Lọc theo TG
      if (filters.tgLinked === 'linked' && !social.tg) return false;
      if (filters.tgLinked === 'unlinked' && social.tg) return false;

      // Lọc theo Gmail
      if (filters.gmailLinked === 'linked' && !social.gmail) return false;
      if (filters.gmailLinked === 'unlinked' && social.gmail) return false;

      return true;
    });

    return filteredWallets.map((w: any) => w.wallet_address.toLowerCase());
  } catch (error) {
    console.error('Error in getTargetWallets:', error);
    return [];
  }
}

/**
 * Gửi thông báo hệ thống đến danh sách ví
 */
export async function sendSystemNotification(wallets: string[], content: string) {
  if (!wallets || wallets.length === 0) return { success: true, count: 0 };

  try {
    const insertData = wallets.map(wallet => ({
      wallet_address: wallet,
      activity_type: 'SYSTEM',
      description: content,
      status: 'completed',
      created_at: new Date().toISOString()
    }));

    const BATCH_SIZE = 100;
    for (let i = 0; i < insertData.length; i += BATCH_SIZE) {
      const batch = insertData.slice(i, i + BATCH_SIZE);
      await callProxy({
        action: 'insert',
        table: 'user_activities',
        data: batch
      });
    }

    return { success: true, count: wallets.length };
  } catch (error: any) {
    console.error('Error in sendSystemNotification:', error);
    return { success: false, message: error.message };
  }
}
